import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

/** POS environments for generated launch URL (value used as URL origin/path before query string). */
const POS_BASE_URL_OPTIONS = [
  {
    id: "prod",
    value: "https://pos.datavision-online.eu/",
    labelKey: "pages.dsokDetails.posUrlBaseUrlProd",
  },
  {
    id: "stage",
    value: "https://pos-staging.datavision-online.eu",
    labelKey: "pages.dsokDetails.posUrlBaseUrlStage",
  },
  {
    id: "dev",
    value: "http://localhost:5173/",
    labelKey: "pages.dsokDetails.posUrlBaseUrlDev",
  },
];

/**
 * Modal to pick base URL + workspace and generate POS launch URL:
 * baseUrl?customerId=…&deviceId=…&workspaceId=…
 */
const PosUrlGenerateModal = ({
  open,
  onClose,
  workspaces,
  customerId,
  deviceUuid,
  customerLoading = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [baseUrlChoice, setBaseUrlChoice] = useState("prod");
  const [workspaceUuid, setWorkspaceUuid] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");

  useEffect(() => {
    if (!open) {
      setBaseUrlChoice("prod");
      setWorkspaceUuid("");
      setGeneratedUrl("");
    }
  }, [open]);

  useEffect(() => {
    setGeneratedUrl("");
  }, [workspaceUuid, baseUrlChoice]);

  const workspaceList = useMemo(() => {
    const raw = Array.isArray(workspaces) ? workspaces : [];
    return raw.filter((w) => {
      if (!w || typeof w !== "object") return false;
      const id = w.uuid ?? w.workspaceId;
      return id != null && String(id).trim() !== "";
    });
  }, [workspaces]);

  const hasWorkspaces = workspaceList.length > 0;
  const awaitingCustomer = Boolean(customerLoading);
  const missingCustomer =
    customerId == null || String(customerId).trim() === "";
  const missingDevice =
    !deviceUuid || String(deviceUuid).trim() === "";

  const selectedBaseUrlEntry = POS_BASE_URL_OPTIONS.find((o) => o.id === baseUrlChoice);
  const selectedBaseUrlValue = selectedBaseUrlEntry?.value?.trim() ?? "";
  const hasBaseUrlSelection =
    baseUrlChoice !== "" && selectedBaseUrlValue !== "";

  const canGenerate =
    !awaitingCustomer &&
    hasWorkspaces &&
    hasBaseUrlSelection &&
    !missingCustomer &&
    !missingDevice &&
    workspaceUuid !== "";

  const handleGenerate = () => {
    if (!canGenerate) return;
    const base = selectedBaseUrlValue.replace(/\/+$/, "");
    const url = `${base}?customerId=${encodeURIComponent(
      String(customerId),
    )}&deviceId=${encodeURIComponent(String(deviceUuid))}&workspaceId=${encodeURIComponent(
      String(workspaceUuid),
    )}`;
    setGeneratedUrl(url);
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      enqueueSnackbar(
        t("pages.dsokDetails.posUrlCopied", {
          defaultValue: "URL was copied to clipboard",
        }),
        { variant: "success" },
      );
    } catch (_e) {
      enqueueSnackbar(
        t("pages.dsokDetails.posUrlCopyError", {
          defaultValue: "Could not copy URL",
        }),
        { variant: "error" },
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("pages.dsokDetails.posUrlModalTitle", {
          defaultValue: "Generovanie POS URL",
        })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {missingCustomer && (
            <Alert severity="warning">
              {t("pages.dsokDetails.posUrlMissingCustomer", {
                defaultValue: "Customer ID is missing.",
              })}
            </Alert>
          )}
          {missingDevice && (
            <Alert severity="warning">
              {t("pages.dsokDetails.posUrlMissingDevice", {
                defaultValue: "Device UUID is missing.",
              })}
            </Alert>
          )}
          {awaitingCustomer && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <span>
                {t("pages.dsokDetails.posUrlLoadingCustomer", {
                  defaultValue: "Načítavam údaje zákazníka…",
                })}
              </span>
            </Stack>
          )}
          {!awaitingCustomer &&
            !hasWorkspaces &&
            !missingCustomer &&
            !missingDevice && (
              <Alert severity="info">
                {t("pages.dsokDetails.posUrlEmptyWorkspaces", {
                  defaultValue:
                    "Pre tohto zákazníka nie sú definované žiadne pracoviská.",
                })}
              </Alert>
            )}
          <FormControl fullWidth disabled={awaitingCustomer}>
            <InputLabel id="pos-url-baseurl-label">
              {t("pages.dsokDetails.posUrlBaseUrlLabel", {
                defaultValue: "BaseUrl",
              })}
            </InputLabel>
            <Select
              labelId="pos-url-baseurl-label"
              label={t("pages.dsokDetails.posUrlBaseUrlLabel", {
                defaultValue: "BaseUrl",
              })}
              value={baseUrlChoice}
              onChange={(e) => setBaseUrlChoice(e.target.value)}
            >
              {POS_BASE_URL_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {t(opt.labelKey, {
                    defaultValue:
                      opt.id === "prod"
                        ? "PROD"
                        : opt.id === "stage"
                          ? "STAGE"
                          : "DEV",
                  })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!hasWorkspaces || awaitingCustomer}>
            <InputLabel id="pos-url-workspace-label">
              {t("pages.dsokDetails.posUrlWorkspaceLabel", {
                defaultValue: "Pracovisko",
              })}
            </InputLabel>
            <Select
              labelId="pos-url-workspace-label"
              label={t("pages.dsokDetails.posUrlWorkspaceLabel", {
                defaultValue: "Pracovisko",
              })}
              value={workspaceUuid}
              onChange={(e) => setWorkspaceUuid(e.target.value)}
            >
              <MenuItem value="">
                <em>
                  {t("pages.dsokDetails.posUrlSelectWorkspace", {
                    defaultValue: "Vyberte pracovisko",
                  })}
                </em>
              </MenuItem>
              {workspaceList.map((w) => {
                const id = w.uuid ?? w.workspaceId ?? "";
                const name =
                  typeof w.name === "string" && w.name.trim() !== ""
                    ? w.name
                    : id || "—";
                return (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {generatedUrl ? (
            <>
              <TextField
                fullWidth
                multiline
                minRows={2}
                InputProps={{ readOnly: true }}
                label={t("pages.dsokDetails.posUrlGeneratedLabel", {
                  defaultValue: "Vygenerovaná URL",
                })}
                value={generatedUrl}
              />
              <Button variant="outlined" onClick={handleCopy}>
                {t("pages.dsokDetails.posUrlCopy", {
                  defaultValue: "Kopírovať URL",
                })}
              </Button>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t("buttons.cancel", { defaultValue: "Zrušiť" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canGenerate}
        >
          {t("pages.dsokDetails.posUrlGenerate", { defaultValue: "Generovať" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PosUrlGenerateModal;
